import { Flex } from "antd";
import { isEmpty, uniq, uniqBy } from "lodash-es";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { DataTableFilter } from "~components/DataTable/types/dataTableFilter.ts";
import { type IMultipleSelectFilterOption, MultipleSelectFilter } from "~components/MultipleSelectFilter/MultipleSelectFilter.tsx";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { TableFilter } from "~enums/tableFilter.ts";
import { DateRangeFilter } from "~pages/Fixtures/FixtureListPage/components";
import { FixturesFilterTypes } from "~pages/Fixtures/FixtureListPage/types";
import { mapMultiSelectOptions } from "~pages/Fixtures/FixtureListPage/utilities";

export interface IFixtureFiltersProperties {
  serverFixtures: any[];
  competitorType: CompetitorTypes;
  currentFilter: { [key: string]: any };
}

export const useGetFixtureTableFilters = ({
  serverFixtures,
  currentFilter,
  competitorType,
}: IFixtureFiltersProperties): {
  filters: DataTableFilter[];
} => {
  const { formatMessage } = useIntl();

  const serverFixturesData = serverFixtures || [];

  const venuesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = serverFixturesData
      ? uniqBy(
          serverFixturesData.map((x) => (isEmpty(x.venue) ? { venueId: "", nameLocal: "" } : x.venue)),
          (x) => x.venueId,
        )
      : [];
    return mapMultiSelectOptions(
      items,
      (x) => x.venueId,
      (x) => x.nameLocal,
    );
  }, [serverFixturesData]);

  const fixtureStatusesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniq(serverFixturesData.map((x) => (isEmpty(x.status) ? "" : x.status)));
    return mapMultiSelectOptions(
      items,
      (x) => x,
      (x) => x,
    );
  }, [serverFixturesData]);

  const poolsOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = serverFixturesData
      ? uniqBy(
          serverFixturesData.map((x) => (isEmpty(x.pool) ? { poolCode: "", nameLocal: "" } : x.pool)),
          (x) => x.poolCode,
        )
      : [];
    return mapMultiSelectOptions(
      items,
      (x) => x.poolCode,
      (x) => x.nameLocal,
    );
  }, [serverFixturesData]);

  const roundNumberOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = serverFixturesData
      ? uniqBy(
          serverFixturesData.map((x) => (isEmpty(x.roundNumber) ? "" : x.roundNumber)),
          (x) => x,
        )
      : [];

    return mapMultiSelectOptions(
      items,
      (x) => x,
      (x) => x,
    );
  }, [serverFixturesData]);

  const competitorOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    if (!serverFixturesData) return [];
    const items = uniqBy(
      serverFixturesData
        .flatMap((x) => [
          isEmpty(x.homeCompetitor?.entity) ? { entityId: "", nameFullLocal: "" } : x.homeCompetitor.entity,
          isEmpty(x.awayCompetitor?.entity) ? { entityId: "", nameFullLocal: "" } : x.awayCompetitor.entity,
        ])
        .filter((x) => !isEmpty(x.entityId)),
      (x) => x.entityId,
    );

    const selected = uniq([...(currentFilter.HomeCompetitors ?? []), ...(currentFilter.AwayCompetitors ?? [])]);

    return mapMultiSelectOptions(
      items,
      (x) => x.entityId,
      (x) => x.nameFullLocal,
      (x) => selected.includes(x.entityId),
    );
  }, [currentFilter.AwayCompetitors, currentFilter.HomeCompetitors, serverFixturesData]);

  const participantsOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    if (!serverFixturesData) return [];

    const items = uniqBy(
      serverFixturesData
        .flatMap((x: any) => [...(x.homeParticipants ?? []), ...(x.awayParticipants ?? [])])
        .map((p: any) => (isEmpty(p?.person) ? { personId: "", nameFullLocal: "" } : p.person))
        .filter((person: any) => !isEmpty(person.personId)),
      (person: any) => person.personId,
    );

    const selected = uniq([...(currentFilter.HomeParticipants ?? []), ...(currentFilter.AwayParticipants ?? [])]);

    return mapMultiSelectOptions(
      items,
      (x: any) => x.personId,
      (x: any) => x.nameFullLocal,
      (x: any) => selected.includes(x.personId),
    );
  }, [serverFixturesData, currentFilter.HomeParticipants, currentFilter.AwayParticipants]);

  const stagesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixturesData.map((x) => (isEmpty(x.stage) ? { stageCode: "", nameLocal: "" } : x.stage)),
      (x) => x.stageCode,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.stageCode,
      (x) => x.nameLocal,
    );
  }, [serverFixturesData]);

  const fixtureTypesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniq(serverFixturesData.map((x) => (isEmpty(x.fixtureType) ? "" : x.fixtureType)));
    return mapMultiSelectOptions(
      items,
      (x) => x,
      (x) => x,
    );
  }, [serverFixturesData]);

  const seriesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixturesData.map((x) => (isEmpty(x.series) ? { seriesCode: "", nameLocal: "" } : x.series)),
      (x) => x.seriesCode,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.seriesCode,
      (x) => x.nameLocal,
    );
  }, [serverFixturesData]);

  const filters = [
    {
      filterKey: FixturesFilterTypes.RoundNumber,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.RoundNumber}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.roundNumber.allOption",
          })}
          options={roundNumberOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.Competitors,
      tableFilterType: TableFilter.Primary,
      show: competitorType === CompetitorTypes.Entity,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.Competitors}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.competitors.allOption",
          })}
          options={competitorOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.Participants,
      tableFilterType: TableFilter.Primary,
      show: competitorType === CompetitorTypes.Person,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.Participants}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.participants.allOption",
          })}
          options={participantsOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.Venues,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.Venues}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.venues.allOption",
          })}
          options={venuesOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.FixtureStatuses,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.FixtureStatuses}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.statuses.allOption",
          })}
          options={fixtureStatusesOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.FixtureTypes,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.FixtureTypes}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.fixtureTypes.allOption",
          })}
          options={fixtureTypesOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.Stages,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.Stages}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.stages.allOption",
          })}
          options={stagesOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.Pools,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.Pools}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.pools.allOption",
          })}
          options={poolsOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.Series,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          filterKey={FixturesFilterTypes.Series}
          value={props.value ?? []}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.series.allOption",
          })}
          options={seriesOptions}
          onChange={props.onChange}
        />
      ),
    },
    {
      filterKey: FixturesFilterTypes.DateRange,
      tableFilterType: TableFilter.Secondary,
      secondaryFilterLabel: (
        <Flex gap={12} align="center" justify="space-between">
          <FormattedMessage id="fixtureListPage.filters.dateRange" />:
          <Flex gap={8} align="center">
            {currentFilter.DateRange?.from?.format("DD/MM/YYYY")}
            {currentFilter.DateRange?.to && ` - ${currentFilter.DateRange.to.format("DD/MM/YYYY")}`}
          </Flex>
        </Flex>
      ),
      secondaryFilterHeader: "advanced",
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <DateRangeFilter
          value={props.value ?? { from: null, to: null }}
          placeholder={formatMessage({
            id: "fixtureListPage.filters.dateRange",
          })}
          onChange={props.onChange}
        />
      ),
    },
  ];
  return { filters };
};
